import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, message } from "@mimber/ui-web"
import { Form, InputField, InputNumberField, SwitchField } from "@mimber/forms"
import { useUpdateTaxRate } from "@mimber/client/mimber-organization"
import type { EditTaxRateFormProps } from "./EditTaxRateForm.types"
import {
  editTaxRateSchema,
  type EditTaxRateData,
} from "./EditTaxRateForm.schema"

export const EditTaxRateForm: React.FunctionComponent<EditTaxRateFormProps> = ({
  taxRate,
  onSuccess,
}) => {
  const form = useForm<EditTaxRateData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(editTaxRateSchema) as any,
    mode: "onBlur",
    defaultValues: {
      name: taxRate.name,
      ratePercentage: taxRate.ratePercentage,
      isActive: taxRate.isActive,
    },
  })

  const mutation = useUpdateTaxRate({
    onSuccess: (response) => {
      message.success("Tax rate updated successfully")
      onSuccess?.(response.data)
    },
    onError: (error) => {
      message.error(`Failed to update tax rate: ${error.message}`)
    },
  })

  const onSubmit = (data: EditTaxRateData) => {
    mutation.mutate({ uuid: taxRate.uuid, data })
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      <InputField
        name="name"
        label="Rate Name"
        placeholder="e.g., CA Sales Tax"
        required
        maxLength={255}
        showCount
      />

      <InputNumberField
        name="ratePercentage"
        label="Rate (%)"
        placeholder="8.25"
        required
        min={0}
        max={100}
        step={0.01}
        precision={2}
        suffix="%"
        help="Applied on top of the subtotal (after discounts), before platform fees"
      />

      <SwitchField
        name="isActive"
        label="Active"
        help="Inactive rates are treated as untaxed even if still assigned to a revenue type"
      />

      <Space style={{ justifyContent: "flex-end", width: "100%" }}>
        <Button type="primary" htmlType="submit" loading={mutation.isPending}>
          Save Changes
        </Button>
      </Space>
    </Form>
  )
}
